import * as React from "react";
import { formatDatetime } from "../../../../utils/date-utils";
import { ProcedureEntity } from "../../../../application/domain/procedures/procedure-entity";
import {
  ApplicationEntity,
  ApplyStatus,
} from "../../../../application/domain/application/application-entity";
import { StatusUpdateModal } from "../../../organisms/smart-apply/modals/status-update-modal";
import { RevertStatusModal } from "../../../organisms/smart-apply/modals/revert-status-modal";
import { useModal } from "../../../../utils/custom-hooks";
import { ApplicantInfo } from "../../../organisms/smart-apply/application-detail/applicant-info";
import { InternalComments } from "./internal-comments/internal-comment";
import { SecondaryButton } from "@graffer-inc/console-design-kit";
import { SmartApplyR } from "../route";
import { SignatureStatusTag } from "../../../molecules/tags/signature-status-tag";
import {
  CustomTag,
  DefaultApplicantSchemaKeys,
} from "@graffer-inc/cloud-gov-core";
import { useRBAC } from "../../../helpers/rbac/use-rbac";
import { Applicant } from "../../../../application/domain/application/application-entity";
import dayjs from "../../../../utils/date-utils";

interface AppDetailBasicInfoProps {
  appl: ApplicationEntity;
  procedure: ProcedureEntity;
  removalIsScheduled: boolean;
  updateApplyStatus: (
    appl: ApplyStatus,
    skipNotification: boolean,
    message?: string,
  ) => Promise<void>;
  onRevertStatusToWip: (internalComment: string) => Promise<void>;
  refreshApplication: (id: string) => Promise<void>;
}

const hasNoEmailFieldJsonOfCustomPlan = (customTag: CustomTag | undefined) => {
  const notEmailCustomTags = [
    "safenet-crisis-four",
    "safenet-crisis-four-from-bank",
    "safenet-crisis-five-pattern-ione",
    "safenet-crisis-five-pattern-ifour",
    "safenet-crisis-five-pattern-ione-from-bank",
    "safenet-crisis-five-pattern-ifour-from-bank",
  ];
  return (
    customTag !== undefined &&
    notEmailCustomTags.some(
      (notEmailCustomTag) => notEmailCustomTag === customTag,
    )
  );
};

export const AppDetailBasicInfo: React.FC<AppDetailBasicInfoProps> = ({
  appl,
  procedure,
  removalIsScheduled,
  updateApplyStatus,
  onRevertStatusToWip,
  refreshApplication,
}) => {
  const statusModal = useModal(false);
  const revertModal = useModal(false);
  const hasPermission = useRBAC();

  const applicant = React.useMemo(
    () =>
      appl.getApplicant(
        // This is a temporary fix.
        // See https://github.com/graffer-inc/cloud-gov/pull/11515
        // See https://github.com/graffer-inc/cloud-gov/pull/12297
        hasNoEmailFieldJsonOfCustomPlan(procedure.customTag)
          ? DefaultApplicantSchemaKeys
          : procedure.getLiveDetail().applicantSchemaKeys,
      ),
    [appl, procedure],
  );

  const getApplicantValue = React.useCallback(
    (key: keyof Applicant) => {
      switch (key) {
        case "email":
          return applicant[key]
            ? `${applicant[key]}（${appl.userIdExists ? "アカウント認証" : "メールアドレス認証"}）`
            : undefined;

        case "birthDate":
          return applicant[key]
            ? dayjs(applicant[key], "YYYY/MM/DD").format("YYYY年 MM月 DD日")
            : undefined;

        default:
          return applicant[key] ?? undefined;
      }
    },
    [applicant, appl.userIdExists],
  );

  return (
    <div className="columns">
      <div className="column is-6">
        {procedure.requireSignature() && (
          <div className="field">
            <label className="label">電子署名の検証ステータス</label>
            <p>
              <SignatureStatusTag appl={appl} />
            </p>
          </div>
        )}
        <div className="field">
          <label htmlFor="name" className="label">
            手続き名称
          </label>
          <div className="control">
            {hasPermission({
              target: procedure,
              requirePermissions: "procedures.read",
            }) ? (
              <a
                href={SmartApplyR.ProcedureDetail.link({ id: procedure.id })}
                target="_blank"
                rel="noopener noreferrer"
              >
                {appl.name}
              </a>
            ) : (
              // 振り分けられた申請などの場合は手続きの閲覧権限がないため、リンクにしない
              <p>{appl.name}</p>
            )}
          </div>
        </div>
        <div className="field">
          <label htmlFor="name" className="label">
            申請者情報
          </label>
          <div className="control">
            <ApplicantInfo
              getApplicantValue={getApplicantValue}
              applicantType={applicant.type}
            />
          </div>
        </div>
        {appl.hasRefKeylessFiles() && (
          <div className="field">
            <label htmlFor="name" className="label">
              添付書類
            </label>
            {appl.refKeyLessFiles.map((f, index) => {
              return (
                <div className="columns" key={f.url}>
                  <div className="column">
                    <p>{f.name}</p>
                  </div>
                  <div className="column has-text-right">
                    <SecondaryButton
                      isSmall={true}
                      text={"ダウンロード"}
                      onClick={() => {
                        window.open(
                          appl.formatFileURLWithIndex(index),
                          "_blank",
                        );
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="field">
          <label htmlFor="name" className="label">
            受付日時
          </label>
          <div className="control">
            <p data-application-prop="createdAt">
              {formatDatetime(appl.createdAt)}
            </p>
          </div>
        </div>
        <div className="field">
          <label htmlFor="name" className="label">
            最終更新日時
          </label>
          <div className="control">
            <p data-application-prop="updatedAt">
              {formatDatetime(appl.updatedAt)}
            </p>
          </div>
        </div>
      </div>
      <div className="column main is-6 has-text-left">
        <InternalComments
          applicationId={appl.id}
          refreshApplication={refreshApplication}
        />
      </div>
      <StatusUpdateModal
        isActive={statusModal.isActive}
        app={appl}
        onClose={statusModal.close}
        onUpdateStatus={updateApplyStatus}
      />
      <RevertStatusModal
        isActive={revertModal.isActive}
        application={appl}
        removalIsScheduled={removalIsScheduled}
        procedureIsArchived={procedure.isArchived()}
        onClose={revertModal.close}
        onRevertStatusToWip={onRevertStatusToWip}
      />
    </div>
  );
};
